import { Feature } from 'ol';
import { Icon, Style, Stroke, Fill, Text } from 'ol/style';
import VectorSource from 'ol/source/Vector';
import { LineString, Point } from 'ol/geom';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import { Coordinate } from 'ol/coordinate';
import { boundingExtent } from 'ol/extent';
import { Color } from 'ol/color';
import ExtremeMapAbstractLayerManager, {
  LayerWithInfo,
  CirclePointStyleParams,
  DirectionArrowStyleParams,
  DIRECTION_POINTER_INTERVAL,
  isAboveZoom1000M,
} from '@/lib/ExtremeMapAbstractLayerManager';
import { IconPath } from '@/models/index';
import type { ExternalCarExt } from '@/models/externalReports/externalCar';
import { FeatureExt } from '@/lib/OlMapManager';
import { NamedVectorLayer } from '@/lib/OlMapWrapper';
import type { ExternalReportPhoto } from '@/models/externalReports/externalReport';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import type { ExternalMtxExt } from '@/models/externalReports/externalMtx';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import { PHOTO_TYPE_WORK_PROGRESS, PHOTO_TYPE_ABNORMAL } from '@/consts/externalReports/standard';
import { getCarDirectionArrowBearing, updateCarBearingRecord } from '@/lib/externalReports/carBearingHelper';
import type { CarBearingRecord } from '@/lib/externalReports/carBearingHelper';

interface ExternalCarIconPath {
  carIconPath: string;
  selectedFrameIconPath: string;
}

interface Opts {
  hideCarIcons: boolean;
  photoTypeVisibility: Record<string, boolean>;
  externalReportUserSettings: ExternalReportSettings | null;
  // 移動中の車両に進行方向の矢印を重ねるか
  showDirectionArrow: boolean;
}

// 全体画面の矢印（ExtremeMapCarLayerManager の CAR_ANGLE_ARROW_SCALE）より大きくする
const CAR_DIRECTION_ARROW_SCALE = 0.4;
// 車両の青丸の半径
const CAR_POINT_RADIUS_PX = 6;
// arrow-small1.png（32×28）の高さ
const CAR_DIRECTION_ARROW_ICON_HEIGHT_PX = 28;
// 矢印の手前の端を青丸の縁に合わせる。anchor は画像の高さに対する割合で、scale 倍した後の距離で効く
const CAR_DIRECTION_ARROW_ANCHOR_Y =
  1 + CAR_POINT_RADIUS_PX / (CAR_DIRECTION_ARROW_ICON_HEIGHT_PX * CAR_DIRECTION_ARROW_SCALE);

const PHOTO_PIN_TEXT_COLOR_MAP: Record<string, string> = {
  [PHOTO_TYPE_ABNORMAL]: '#000000',
  [PHOTO_TYPE_WORK_PROGRESS]: '#FFFFFF',
};

const MOVING_LINE_COLOR = 'rgba(127, 127, 127, 0.8)';
const WORKING_LINE_COLOR = 'rgba(56, 241, 215, 0.8)';
const ZOOM_LEVEL_LTE_1000M_LINE_WIDTH = 4;
const ZOOM_LEVEL_GT_1000M_LINE_WIDTH = 7;
const MOVING_MTX_POINT_FILL_COLOR: Color = [157, 157, 157, 1.0];
const MOVING_MTX_POINT_STROKE_COLOR: Color = [101, 101, 101, 1.0];
const WORKING_MTX_POINT_FILL_COLOR: Color = [56, 241, 215, 1.0];
const WORKING_MTX_POINT_STROKE_COLOR: Color = [37, 161, 144, 1.0];
const ZOOM_LEVEL_LTE_1000M_MTX_POINT_RADIUS = 2.4;
const ZOOM_LEVEL_GT_1000M_MTX_POINT_RADIUS = 3.6;

export default class ExtremeMapExternalCarLayerManager extends ExtremeMapAbstractLayerManager {
  layerName: string;
  opts: Opts;
  extremeMap: InstanceType<typeof ExtremeMap> | null;
  // 車両ID → 向きを最後に更新した位置と方位。移動が閾値未満の描画では前回の方位を使い続ける
  private carBearingRecords_: Map<number, CarBearingRecord> = new Map();
  constructor(opts: Opts) {
    super();
    this.layerName = 'standard-cars';
    this.layerInfo.onLayerClick = () => {};
    this.opts = opts;
    this.extremeMap = null;
  }

  getStandardCarIconPath(car: ExternalCarExt): ExternalCarIconPath {
    if (!this.opts.externalReportUserSettings) {
      return {
        carIconPath: '',
        selectedFrameIconPath: '',
      };
    }
    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    const externalReportType = this.opts.externalReportUserSettings.external_report_type;
    const standardCarIconPrefix = `/static/img/externalReports/${externalReportType}/car_`;
    const statusPrefix = car.isMoving ? statusChoices.running.key : statusChoices.stopped.key;
    const carIconPath = `${standardCarIconPrefix}${statusPrefix}_default.png`;
    const selectedFrameIconPath = '/static/img/car_selected.png';
    return { carIconPath, selectedFrameIconPath };
  }

  getCarStyles(car: ExternalCarExt, zIndex: number): Style[] | null {
    if (!car.shouldShow) {
      return null;
    }

    const ret: Style[] = [];
    const { carIconPath, selectedFrameIconPath } = this.getStandardCarIconPath(car);

    ret.push(new Style({
      image: new Icon({
        src: carIconPath,
        anchor: [0.5, 0.95],
        anchorXUnits: IconAnchorUnits.FRACTION,
        anchorYUnits: IconAnchorUnits.FRACTION,
        scale: 0.25,
      }),
      zIndex,
    }));
    if (car.isSelected) {
      ret.push(new Style({
        image: new Icon({
          src: selectedFrameIconPath,
          anchor: [0.5, 0.95],
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          scale: 0.25,
          opacity: 1.0,
        }),
        zIndex: zIndex + 1,
      }));
    }
    if (car.isWorking) {
      ret.push(new Style({
        image: new Icon({
          src: '/static/img/car_is_working.png',
          anchor: [2.5, 6],
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          scale: 0.02,
          opacity: 1.0,
        }),
        zIndex: zIndex + 2,
      }));
    }

    return ret;
  }
  getCarFeature(car: ExternalCarExt, zIndex: number): Feature {
    const feat = new Feature({
      geometry: car.pos ? new Point(car.pos) : undefined,
    });
    feat.setId(car.id);
    feat.setStyle(this.getCarStyles(car, zIndex));
    (feat as FeatureExt).onClickFunc = () => {
      if (this.extremeMap) {
        this.extremeMap.onClickExternalCar(car);
      }
    };
    return feat;
  }

  getIconPath_(photo: ExternalReportPhoto): IconPath {
    if (!this.opts.externalReportUserSettings) {
      return {
        iconPath: '',
        selectedFramePath: '',
      };
    }

    const externalReportType = this.opts.externalReportUserSettings.external_report_type;
    const iconPath = `/static/img/externalReports/${externalReportType}/report_photo_${photo.photoType}_pin.png`;
    const selectedFramePath = '/static/img/pin_selected.png';
    return { iconPath, selectedFramePath };
  }

  getResourceStyles_(photos: ExternalReportPhoto[], zIndex: number): Style[] {
    const ret: Style[] = [];
    const firstPhoto = photos[0];
    const { iconPath, selectedFramePath } = this.getIconPath_(firstPhoto);
    if (firstPhoto.isSelected) {
      ret.push(new Style({
        image: new Icon({
          src: selectedFramePath,
          anchor: [0.5, 0.82],
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          scale: 0.30,
          opacity: 1.0,
        }),
        zIndex: zIndex,
      }));
    }
    const textFontSize = photos.length > 1 ? 'bold 15px sans-serif' : 'bold 16px sans-serif';
    const textOffsetX = photos.length > 1 ? -2 : 0;
    const textColor = PHOTO_PIN_TEXT_COLOR_MAP[firstPhoto.photoType] ?? '#000000';
    ret.push(new Style({
      image: new Icon({
        src: iconPath,
        anchor: [0.5, 0.82],
        anchorXUnits: IconAnchorUnits.FRACTION,
        anchorYUnits: IconAnchorUnits.FRACTION,
        scale: 0.30,
      }),
      text: new Text({
        text: firstPhoto.dispOrder.toString(),
        font: textFontSize,
        fill: new Fill({ color: textColor }),
        offsetX: textOffsetX,
        offsetY: -23,
        rotation: 0,
      }),
      zIndex: zIndex,
    }));
    if (photos.length > 1) {
      ret.push(new Style({
        text: new Text({
          text: '+',
          font: 'bold 12px sans-serif',
          fill: new Fill({ color: textColor }),
          offsetX: 6,
          offsetY: -23,
          rotation: 0,
        }),
        zIndex: zIndex,
      }));
    }
    return ret;
  }

  // 今回位置で向きの記録を更新し、方位（ラジアン）を返す。一度も閾値以上動いていなければ null
  // 前回描画時の位置との差分で向きを決めるため、画面を開いた直後は閾値以上動くまで矢印が出ない。
  // また位置の更新が途切れた後に離れた場所で再出現すると、次のポーリングまでは旧位置→新位置の直線方向が表示される
  updateCarBearing(car: ExternalCarExt): number | null {
    const current = { lat: parseFloat(car.lat), lon: parseFloat(car.lon) };
    const bearingRecord = updateCarBearingRecord(this.carBearingRecords_.get(car.id) ?? null, current);
    if (!bearingRecord) return null;

    this.carBearingRecords_.set(car.id, bearingRecord);
    return bearingRecord.bearing;
  }

  getCarPointFeature(car: ExternalCarExt, zIndexStart: number, arrowBearing: number | null): Feature {
    const zIndex = zIndexStart;
    const feat = new Feature({
      geometry: car.pos ? new Point(car.pos) : undefined,
    });
    const pointStyleParams: CirclePointStyleParams = {
      radius: CAR_POINT_RADIUS_PX,
      fillColor: [102, 194, 255, 0.8],
      strokeColor: [0, 122, 204, 0.8],
      zIndex,
    };
    const pointStyle = this.getCirclePointStyle(pointStyleParams);
    if (arrowBearing === null) {
      feat.setStyle(pointStyle);
    } else {
      const arrowStyle = this.getCarAngleArrowStyle({
        angleDegrees: this.toDegrees(arrowBearing),
        scale: CAR_DIRECTION_ARROW_SCALE,
        anchorY: CAR_DIRECTION_ARROW_ANCHOR_Y,
        zIndex,
      });
      feat.setStyle([pointStyle, arrowStyle]);
    }
    (feat as FeatureExt).onClickFunc = () => {
      if (this.extremeMap) {
        this.extremeMap.onClickExternalCar(car);
      }
    };
    return feat;
  }

  createLineFeature_(points: Coordinate[], zIndex: number, status: string): Feature | null {
    // need at least 2 points to make a line.
    if (points.length < 2) {
      return null;
    }
    const line = new Feature({
      geometry: new LineString(points),
    });
    if (!this.opts.externalReportUserSettings) {
      return line;
    }
    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    const lineColor = status === statusChoices.running.key ? MOVING_LINE_COLOR : WORKING_LINE_COLOR;
    const styleLte = new Style({
      stroke: new Stroke({ color: lineColor, width: ZOOM_LEVEL_LTE_1000M_LINE_WIDTH }),
      zIndex,
    });
    const styleGt = new Style({
      stroke: new Stroke({ color: lineColor, width: ZOOM_LEVEL_GT_1000M_LINE_WIDTH }),
      zIndex,
    });
    line.setStyle((_feat, resolution) => isAboveZoom1000M(resolution) ? styleGt : styleLte);
    (line as FeatureExt).onClickFunc = () => {};
    return line;
  }

  createCarLineFeatures_(mtxs: ExternalMtxExt[], zIndexStart: number):
    { lineFeats: Feature[]; lineZIndex: number; posCoords: Coordinate[] } {
    const lineFeats: Feature[] = [];
    const posCoords: Coordinate[] = [];
    if (!this.opts.externalReportUserSettings) {
      return { lineFeats, lineZIndex: zIndexStart, posCoords };
    }

    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    let lineZIndex = zIndexStart;
    let movingPoints: Coordinate[] = [];
    let standardPoints: Coordinate[] = [];
    let currentStatus = statusChoices.running.key;
    const len = mtxs.length;
    mtxs.forEach((mtx, i) => {
      const point = this.convCoord(mtx);
      if (mtx.status === statusChoices.running.key) {
        movingPoints.push(point);
      }
      if (mtx.status === statusChoices.working.key) {
        standardPoints.push(point);
      }
      posCoords.push(point);
      if (i === 0) {
        currentStatus = mtx.status;
        return;
      }
      if (currentStatus !== mtx.status || i === len - 1) {
        if (currentStatus === statusChoices.running.key) {
          movingPoints.push(point);
          const lineFeat = this.createLineFeature_(movingPoints, lineZIndex++, currentStatus);
          if (lineFeat) {
            lineFeats.push(lineFeat);
          }
          movingPoints = [];
        }
        if (currentStatus === statusChoices.working.key) {
          standardPoints.push(point);
          const lineFeat = this.createLineFeature_(standardPoints, lineZIndex++, currentStatus);
          if (lineFeat) { lineFeats.push(lineFeat); }
          standardPoints = [];
        }
        currentStatus = mtx.status;
      }
    });
    return { lineFeats, lineZIndex, posCoords };
  }

  createMtxPointFeatures_(mtxs: ExternalMtxExt[], zIndexStart: number):
    { mtxPointFeats: Feature[]; mtxPointZIndex: number } {
    const mtxPointFeats: Feature[] = [];
    if (!this.opts.externalReportUserSettings) {
      return { mtxPointFeats, mtxPointZIndex: zIndexStart };
    }

    const statusChoices = this.opts.externalReportUserSettings.choices.status;
    let mtxPointZIndex = zIndexStart;
    mtxs.forEach((mtx, idx) => {
      const feat = new Feature({
        geometry: new Point(this.convCoord(mtx)),
      });
      const fillColor =
        mtx.status === statusChoices.running.key
          ? MOVING_MTX_POINT_FILL_COLOR
          : WORKING_MTX_POINT_FILL_COLOR;
      const strokeColor =
        mtx.status === statusChoices.running.key
          ? MOVING_MTX_POINT_STROKE_COLOR
          : WORKING_MTX_POINT_STROKE_COLOR;
      const hasNext = idx < mtxs.length - 1;
      const rotation = hasNext ? this.getBearing(mtx, mtxs[idx + 1]) : 0;

      const showDirectionIcon = hasNext && idx % DIRECTION_POINTER_INTERVAL === 0;
      if (showDirectionIcon) {
        const pointStyleParams: DirectionArrowStyleParams = {
          zIndex: mtxPointZIndex++,
          rotation,
        };
        const featStyle = this.getDirectionArrowStyle(pointStyleParams);
        feat.setStyle(featStyle);
      } else {
        const zIdx = mtxPointZIndex++;
        const styleLte = this.getCirclePointStyle({ radius: ZOOM_LEVEL_LTE_1000M_MTX_POINT_RADIUS, fillColor, strokeColor, zIndex: zIdx });
        const styleGt = this.getCirclePointStyle({ radius: ZOOM_LEVEL_GT_1000M_MTX_POINT_RADIUS, fillColor, strokeColor, zIndex: zIdx });
        feat.setStyle((_f, resolution) => isAboveZoom1000M(resolution) ? styleGt : styleLte);
      }
      (feat as FeatureExt).onClickFunc = () => {
        if (this.extremeMap) {
          this.extremeMap.onClickExternalMtx(mtx);
        }
      };
      mtxPointFeats.push(feat);
    });
    return { mtxPointFeats, mtxPointZIndex };
  }

  createPhotoFeatures_(
    photos: ExternalReportPhoto[],
    zIndexStart: number,
    photoTypeVisibility: Record<string, boolean>,
  ): { photoFeats: Feature[]; photoZIndex: number } {
    const photoFeats: Feature[] = [];
    let photoZIndex = zIndexStart;
    photos.forEach(photo => {
      if (photoTypeVisibility[photo.photoType] !== undefined && photoTypeVisibility[photo.photoType] === false) return;
      const samePosPhoto = (photo.samePosPhotos && photo.samePosPhotos.length > 0)
        ? [...photo.samePosPhotos]
          .filter(samePhoto => samePhoto.photoType === photo.photoType)
          .sort((a, b) => a.dispOrder - b.dispOrder)
        : [photo];
      if (photo.id !== samePosPhoto[0].id) { return; }
      const feat = new Feature({
        geometry: new Point(this.convCoord({
          lon: parseFloat((photo.lon || 0).toString()),
          lat: parseFloat((photo.lat || 0).toString()),
        })),
      });
      feat.setStyle(this.getResourceStyles_(samePosPhoto, photoZIndex++));
      (feat as FeatureExt).onClickFunc = () => {
        if (this.extremeMap) {
          this.extremeMap.onClickExternalReportPhoto(photo);
        }
      };
      photoFeats.push(feat);
    });

    return { photoFeats, photoZIndex };
  }

  createCarLayer_(
    cars: ExternalCarExt[],
    { hideCarIcons, photoTypeVisibility, showDirectionArrow }: Opts,
  ): void {
    const features: Feature[] = [];
    let zIndex = 1;
    const carPosCoords: Coordinate[] = [];
    // 配列の先頭側にある方を上にする
    [...cars].reverse().forEach(car => {
      const bearing = showDirectionArrow ? this.updateCarBearing(car) : null;
      if (!car.shouldShow) {
        return;
      }

      if (car.isSelected && car.reportExt) {
        // 車両移動の軌跡を描画する
        const { lineFeats, lineZIndex, posCoords } = this.createCarLineFeatures_(car.reportExt.mtxsExt, zIndex);
        features.push(...lineFeats);
        zIndex = lineZIndex;
        carPosCoords.push(...posCoords);

        // mtxsの点を描画する
        const { mtxPointFeats, mtxPointZIndex } = this.createMtxPointFeatures_(car.reportExt.mtxsExt, zIndex);
        features.push(...mtxPointFeats);
        zIndex = mtxPointZIndex;

        // 写真のpinを描画する
        const { photoFeats, photoZIndex } = this.createPhotoFeatures_(
          car.reportExt.photosExt,
          zIndex,
          photoTypeVisibility,
        );
        features.push(...photoFeats);
        zIndex = photoZIndex;
      }

      // 車両の現時点の位置の点を描画する
      // 車両非表示時は、全体画面と同様に移動中車両を青丸+矢印で表示する
      const arrowBearing = getCarDirectionArrowBearing({
        showDirectionArrow,
        hideCarIcons,
        isMoving: car.isMoving,
        bearing,
      });
      const carPointFeat = this.getCarPointFeature(car, zIndex++, arrowBearing);
      features.push(carPointFeat);

      if (!hideCarIcons) {
        features.push(this.getCarFeature(car, zIndex++));
        zIndex += 5; // styleが複数の場合が有りえるので適当に増やす
      }
    });

    const layer = new NamedVectorLayer('standard-cars', {
      source: new VectorSource({ features }),
    });
    const extent = carPosCoords.length > 0
      ? boundingExtent(carPosCoords) : null;

    this.layer = layer;
    this.layerInfo.extent = extent;
    this.layerInfo.onLayerClick = ({ event, feature }) => {
      // 重なってたりする場合はそれぞれ飛んでくるので、一回で止める
      if (!event || event.originalEvent.defaultPrevented) { return; }
      event.preventDefault();
      if (feature) {
        (feature as FeatureExt).onClickFunc();
      }
    };
  }

  prepareLayer(
    extremeMap: InstanceType<typeof ExtremeMap>,
    cars: ExternalCarExt[],
    opts: Opts,
  ): LayerWithInfo {
    const carsConv = cars.map(car => {
      car.pos = this.convCoord({
        lon: parseFloat(car.lon),
        lat: parseFloat(car.lat),
      });
      return car;
    });
    this.opts = opts;
    this.extremeMap = extremeMap;
    this.createCarLayer_(carsConv, opts);
    return this.getLayer();
  }

  refreshLayerOnZoomChange(): void {
    // 各FeatureのStyleはresolution依存のStyleFunctionになっており、
    // OLがview変更時に自動で再評価するためレイヤー再構築は不要。
  }
}
